import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ClosureDto } from '../entities/component-entities/closure-dto';
import { InMoldLabelDto } from '../entities/component-entities/in-mold-label-dto';
import { InlayDto } from '../entities/component-entities/inlay-dto';
import { LabelDto } from '../entities/component-entities/label-dto';
import { MainBodyDto } from '../entities/component-entities/main-body-dto';
import { PackagingAidDto } from '../entities/component-entities/packaging-aid-dto';
import { PackagingComponentDto } from '../entities/component-entities/packaging-component-dto';
import { SleeveDto } from '../entities/component-entities/sleeve-dto';
import { CreateDataExchangeRequestDto } from '../entities/data-exchange-request-entities/create-data-exchange-request-dto';
import { DataExchangeCommentDto } from '../entities/data-exchange-request-entities/data-exchange-comment-dto';
import { DataExchangeNotificationDto } from '../entities/data-exchange-request-entities/data-exchange-notification-dto';
import { DataExchangeRoleDto } from '../entities/data-exchange-request-entities/data-exchange-role-dto';
import { DataExchangeRequestDto } from '../entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeSubRequestDto } from '../entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { DataExchangeSubRequestInfoDto } from '../entities/data-exchange-request-entities/data-exchange-sub-request-info-dto';
import { MarkDataExchangeNotificationsSeenDto } from '../entities/data-exchange-request-entities/mark-data-exchange-notifications-seen-dto';
import { PaginationQueryDto } from '../entities/pagination-query-dto';
import { PaginationResponseDto } from '../entities/pagination-response-dto';
import { UpdateDataExchangeRequestStatusDto } from '../entities/data-exchange-request-entities/update-data-exchange-request-status-dto';
import { UpdateDataExchangeRequestDeadlineDto } from '../entities/data-exchange-request-entities/update-data-exchange-request-deadline-dto';
import { MultiMaterialCompositeDto } from '../entities/material-entities/multi-material-composite-dto';
import { PackagingSystemDto } from '../entities/packaging-system-entities/packaging-system-dto';
import { PackagingUnitDto } from '../entities/packaging-unit-entities/packaging-unit-dto';
import { ParentApiService } from './parent-api-service';

@Injectable({ providedIn: 'root' })
export class DataExchangeRequestApiService extends ParentApiService {
  private readonly baseUrl = environment.webApiAddress + '/api/data-exchange-requests';
  private readonly subRequestsUrl = `${this.baseUrl}/sub-requests`;

  private readonly CACHE_DURATION_MS = 5000; // 5 seconds

  private reloadTrigger$ = new BehaviorSubject<void>(undefined);
  private lastLoadTime: number | null = null;

  public unseenSentDataExchangeRequests$: Observable<number>;
  public unseenReceivedDataExchangeRequests$: Observable<number>;

  constructor(private http: HttpClient) {
    super();

    const unseenNotifications$ = this.reloadTrigger$.pipe(
      tap(() => this.lastLoadTime = Date.now()),
      switchMap(() => this.getNotifications(true).pipe(
        catchError(() => EMPTY)
      )),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.unseenSentDataExchangeRequests$ = unseenNotifications$.pipe(map(notifications => this.countRequestsWithNotifications(notifications, DataExchangeRoleDto.Sender)));
    this.unseenReceivedDataExchangeRequests$ = unseenNotifications$.pipe(map(notifications => this.countRequestsWithNotifications(notifications, DataExchangeRoleDto.Receiver)));
  }

  private countRequestsWithNotifications(notifications: DataExchangeNotificationDto[], role: DataExchangeRoleDto): number {
    return new Set(notifications.filter(n => n.role === role).map(n => n.dataExchangeRequestId)).size;
  }

  reloadUnseenDataExchangeRequests(forceReload = false): void {
    if (forceReload || this.shouldReload()) {
      this.reloadTrigger$.next();
    }
  }

  private shouldReload(): boolean {
    if (this.lastLoadTime === null) {
      return true;
    }
    return Date.now() - this.lastLoadTime >= this.CACHE_DURATION_MS;
  }

  createDataExchangeRequest(dto: CreateDataExchangeRequestDto, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto> {
    return this.http.post<DataExchangeRequestDto>(this.baseUrl, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateStatus(id: number, dto: UpdateDataExchangeRequestStatusDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${id}/status`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  withdrawDataExchangeRequest(id: number, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/withdraw`, null, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  approveDataExchangeRequest(id: number, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/approve`, null, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateSubRequestStatus(id: number, dto: UpdateDataExchangeRequestStatusDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.patch<void>(`${this.subRequestsUrl}/${id}/status`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getNotifications(stayOnPageIfError?: boolean): Observable<DataExchangeNotificationDto[]> {
    return this.http.get<DataExchangeNotificationDto[]>(`${this.baseUrl}/notifications`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  markNotificationsSeen(dto: MarkDataExchangeNotificationsSeenDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/notifications/seen`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateDeadline(id: number, dto: UpdateDataExchangeRequestDeadlineDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/deadline`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getRequestById(id: number, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto> {
    return this.http.get<DataExchangeRequestDto>(`${this.baseUrl}/by-id?id=${id}`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getSentRequestsByPage(query: PaginationQueryDto, stayOnPageIfError?: boolean): Observable<PaginationResponseDto<DataExchangeRequestDto>> {
    return this.http.post<PaginationResponseDto<DataExchangeRequestDto>>(`${this.baseUrl}/sent/by-page`, query, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getReceivedRequestsByPage(query: PaginationQueryDto, stayOnPageIfError?: boolean): Observable<PaginationResponseDto<DataExchangeRequestDto>> {
    return this.http.post<PaginationResponseDto<DataExchangeRequestDto>>(`${this.baseUrl}/received/by-page`, query, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getSubRequestsByPage(query: PaginationQueryDto, stayOnPageIfError?: boolean): Observable<PaginationResponseDto<DataExchangeSubRequestDto>> {
    return this.http.post<PaginationResponseDto<DataExchangeSubRequestDto>>(`${environment.webApiAddress}/api/data-exchange-sub-requests/by-page`, query, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  addComment(requestId: number, comment: string, stayOnPageIfError?: boolean): Observable<DataExchangeCommentDto> {
    return this.http.post<DataExchangeCommentDto>(`${this.baseUrl}/${requestId}/comments`, { comment }, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  addSubRequestComment(subRequestId: number, comment: string, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.subRequestsUrl}/${subRequestId}/comments`, { comment }, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getSubRequestInfo(id: number, stayOnPageIfError?: boolean): Observable<DataExchangeSubRequestInfoDto> {
    return this.http.get<DataExchangeSubRequestInfoDto>(`${this.subRequestsUrl}/${id}/info`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getOngoingDataExchangeRequestsForMultiMaterial(packagingId: number, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto[]> {
    return this.http.get<DataExchangeRequestDto[]>(`${this.baseUrl}/active-by-packaging/multi-material/${packagingId}`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getOngoingDataExchangeRequestsForPackagingSystem(packagingId: number, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto[]> {
    return this.http.get<DataExchangeRequestDto[]>(`${this.baseUrl}/active-by-packaging/packaging-system/${packagingId}`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getOngoingDataExchangeRequestsForPackagingUnit(packagingId: number, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto[]> {
    return this.http.get<DataExchangeRequestDto[]>(`${this.baseUrl}/active-by-packaging/packaging-unit/${packagingId}`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getOngoingDataExchangeRequestsForPackagingComponent(packagingId: number, stayOnPageIfError?: boolean): Observable<DataExchangeRequestDto[]> {
    return this.http.get<DataExchangeRequestDto[]>(`${this.baseUrl}/active-by-packaging/packaging-component/${packagingId}`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getSubRequestMultiMaterial(id: number, stayOnPageIfError?: boolean): Observable<MultiMaterialCompositeDto> {
    return this.http.get<MultiMaterialCompositeDto>(`${this.subRequestsUrl}/${id}/multi-material`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateSubRequestMultiMaterial(id: number, dto: MultiMaterialCompositeDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    let params = new HttpParams();
    if (submit != null) {
      params = params.set('submit', submit);
    }
    return this.http.put<void>(`${this.subRequestsUrl}/${id}/multi-material`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      params,
    });
  }

  getSubRequestPackagingComponent(id: number, stayOnPageIfError?: boolean): Observable<PackagingComponentDto> {
    return this.http.get<PackagingComponentDto>(`${this.subRequestsUrl}/${id}/packaging-component`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateSubRequestPackagingComponent(
    id: number, dto: PackagingComponentDto, packagingComponentType: string, submit?: boolean, stayOnPageIfError?: boolean
  ): Observable<void> {
    const componentPath = this.getSubRequestComponentPath(packagingComponentType);
    let params = new HttpParams();
    if (submit != null) {
      params = params.set('submit', submit);
    }
    return this.http.put<void>(`${this.subRequestsUrl}/${id}/packaging-component/${componentPath}`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      params,
    });
  }

  private getSubRequestComponentPath(componentTypeName: string): string {
    switch (componentTypeName.toLowerCase()) {
      case 'closure': return 'closure';
      case 'inlay': return 'inlay';
      case 'in-mold label':
      case 'inmoldlabel': return 'in-mold-label';
      case 'label':
      case 'banderole': return 'label';
      case 'packaging aid':
      case 'packagingaid': return 'packaging-aid';
      case 'sleeve':
      case 'wrap': return 'sleeve';
      case 'main body':
      case 'mainbody': return 'packaging-main-body';
      default: return componentTypeName.toLowerCase();
    }
  }

  updateSubRequestClosure(id: number, dto: ClosureDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'closure', submit, stayOnPageIfError);
  }

  updateSubRequestInMoldLabel(id: number, dto: InMoldLabelDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'in-mold label', submit, stayOnPageIfError);
  }

  updateSubRequestInlay(id: number, dto: InlayDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'inlay', submit, stayOnPageIfError);
  }

  updateSubRequestLabel(id: number, dto: LabelDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'label', submit, stayOnPageIfError);
  }

  updateSubRequestPackagingAid(id: number, dto: PackagingAidDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'packaging aid', submit, stayOnPageIfError);
  }

  updateSubRequestMainBody(id: number, dto: MainBodyDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'main body', submit, stayOnPageIfError);
  }

  updateSubRequestSleeve(id: number, dto: SleeveDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    return this.updateSubRequestPackagingComponent(id, dto, 'sleeve', submit, stayOnPageIfError);
  }

  getSubRequestPackagingSystem(id: number, stayOnPageIfError?: boolean): Observable<PackagingSystemDto> {
    return this.http.get<PackagingSystemDto>(`${this.subRequestsUrl}/${id}/packaging-system`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateSubRequestPackagingSystem(id: number, dto: PackagingSystemDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    let params = new HttpParams();
    if (submit != null) {
      params = params.set('submit', submit);
    }
    return this.http.put<void>(`${this.subRequestsUrl}/${id}/packaging-system`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      params,
    });
  }

  getSubRequestPackagingUnit(id: number, stayOnPageIfError?: boolean): Observable<PackagingUnitDto> {
    return this.http.get<PackagingUnitDto>(`${this.subRequestsUrl}/${id}/packaging-unit`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  updateSubRequestPackagingUnit(id: number, dto: PackagingUnitDto, submit?: boolean, stayOnPageIfError?: boolean): Observable<void> {
    let params = new HttpParams();
    if (submit != null) {
      params = params.set('submit', submit);
    }
    return this.http.put<void>(`${this.subRequestsUrl}/${id}/packaging-unit`, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      params,
    });
  }
}
